import React, { useState, useEffect, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSuccessionTheme } from '../SuccessionThemeProvider';
import CurrencyInput from 'react-currency-input-field';
import { currencySymbol } from '../../../utils/formatters';

// Min/Max helper copy and the over-max error: whole dollars only — drop cents (e.g. $100,000, not $100,000.00).
// Coerce first so string-serialized decimals (Rails) are handled; keep the locale's separators.
const formatLimit = (val, isNumber) => {
  const n = Number(val) || 0;
  const formatted = n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return isNumber ? formatted : `${currencySymbol()}${formatted}`;
};

const toRaw = (val) => (val === '' || val === null || val === undefined ? '' : String(val));

// Simple input component without sliders.
// The field shows what was typed (including empty, or "1234." mid-way to cents) and never rewrites it: a value
// outside min/max is reported by FinancialDetails' validation, which blocks Next. Clamping here on every keystroke
// turned a cleared Relationships field into 1, so typing 222 became 1222 and then the max (RJ: "222 becomes 250").
const FinancialInput = ({ label, name, value, onChange, prefix = currencySymbol(), isNumber = false, min = 0, max, minLabel, maxLabel }) => {
  const [raw, setRaw] = useState(toRaw(value));

  // Follow the parent only when it holds a different number than the one typed, e.g. the wizard's Reset button.
  useEffect(() => {
    if ((parseFloat(raw) || 0) !== (Number(value) || 0)) setRaw(toRaw(value));
  }, [value]);

  const formatDisplayValue = (val) => formatLimit(val, isNumber);

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ marginBottom: '10px' }}>
        <label style={{ fontWeight: 'bold', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
          {label}
        </label>
      </div>
      
      <CurrencyInput
        name={name}
        value={raw}
        onValueChange={(val) => {
          setRaw(val ?? '');
          onChange(name, parseFloat(val) || 0);
        }}
        style={{ 
          width: '100%', 
          padding: '12px 16px', 
          border: '1px solid var(--succession-border)', 
          borderRadius: '8px',
          fontSize: '16px',
          backgroundColor: 'var(--succession-bg-primary)',
          color: 'var(--succession-text-primary)'
        }}
        placeholder={isNumber ? "0" : "$0.00"}
        prefix={isNumber ? "" : "$"}
        decimalsLimit={isNumber ? 0 : 2}
        allowNegativeValue={false}
        allowDecimals={!isNumber}
        min={min}
        max={max}
      />
      
      {/* Min/Max Display */}
      <div style={{ 
        fontSize: '12px', 
        color: 'var(--succession-text-secondary)', 
        marginTop: '4px',
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <span>{minLabel || 'Min:'} {formatDisplayValue(min)}</span>
        <span>{maxLabel || 'Max:'} {formatDisplayValue(max)}</span>
      </div>
    </div>
  );
};

const FinancialDetails = ({ formData, setFormData, handleCurrencyChange, company, partialSalesPropertyCasualty, user, onValidationChange }) => {
  const intl = useIntl();
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();
  const [errors, setErrors] = useState({});

  // Get max values from company with defaults
  const maxClients = company?.max_partial_sale_clients || 500;
  const maxAum = company?.max_partial_sale_aum || 500000000;
  const maxFeeBasedRevenue = company?.max_partial_sale_fee_based_revenue || 500000;
  const maxTrailsRevenue = company?.max_partial_sale_trails_revenue || 500000;
  const maxDirectRevenue = company?.max_partial_sale_direct_revenue || 500000;
  const maxCommissionRevenue = company?.max_partial_sale_commission_revenue || 500000;

  // Each field's max, used for the "Max:" helper and the over-max errors: the seller's own figure, capped by the company.
  const limits = {
    numberOfClients: Math.min(user?.number_of_clients || maxClients, maxClients),
    assetsUnderManagement: Math.min(user?.assets_under_management || maxAum, maxAum),
    feeBasedRevenue: Math.min(
      partialSalesPropertyCasualty ? (user?.recurring_revenue || maxFeeBasedRevenue) : (user?.fee_based_revenue || maxFeeBasedRevenue),
      maxFeeBasedRevenue
    ),
    trailerRevenue: Math.min(user?.trailer_revenue || maxTrailsRevenue, maxTrailsRevenue),
    directRevenue: Math.min(user?.direct_revenue || maxDirectRevenue, maxDirectRevenue),
    commissionRevenue: Math.min(
      partialSalesPropertyCasualty ?
        Math.max((user?.annual_revenue || 0) - (user?.recurring_revenue || 0), maxCommissionRevenue) :
        (user?.transactional_revenue || maxCommissionRevenue),
      maxCommissionRevenue
    ),
  };

  const showTrailsRevenue = !partialSalesPropertyCasualty;
  const showDirectRevenue = !partialSalesPropertyCasualty && !!company && !company.feature_types.includes('hide_direct_revenue');

  // Report validity to the wizard only when it flips, so typing doesn't re-render the whole wizard on every keystroke.
  const lastReportedHasErrors = useRef(null);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.numberOfClients || formData.numberOfClients < 1) {
      newErrors.numberOfClients = intl.formatMessage({ id: 'succession.partial_sales.financial_details.error_relationships_required' });
    }

    const maxRelationships = limits.numberOfClients;
    if (formData.numberOfClients > maxRelationships) {
      newErrors.numberOfClients = intl.formatMessage(
        { id: 'succession.partial_sales.financial_details.error_relationships_max' },
        { max: maxRelationships }
      );
    }

    // Assets under management validation - only if not Property & Casualty
    if (!partialSalesPropertyCasualty) {
      if (!formData.assetsUnderManagement || formData.assetsUnderManagement <= 0) {
        newErrors.assetsUnderManagement = intl.formatMessage({ id: 'succession.partial_sales.financial_details.error_aum_required' });
      }
    } else {
      // For Property & Casualty, validate Total Premium
      if (!formData.assetsUnderManagement || formData.assetsUnderManagement <= 0) {
        newErrors.assetsUnderManagement = intl.formatMessage({ id: 'succession.partial_sales.financial_details.error_total_premium_required' });
      }
    }

    // At least one revenue field should be filled
    const revenueFields = ['feeBasedRevenue', 'commissionRevenue'];

    // Add conditional revenue fields based on feature flags
    if (!partialSalesPropertyCasualty) {
      revenueFields.push('trailerRevenue');
      if (!company?.feature_types?.includes('hide_direct_revenue')) {
        revenueFields.push('directRevenue');
      }
    }

    const hasRevenue = revenueFields.some(field => formData[field] && formData[field] > 0);

    if (!hasRevenue) {
      newErrors.revenue = intl.formatMessage({ id: 'succession.partial_sales.financial_details.error_revenue_required' });
    }

    // Over-max money values are kept as typed and flagged here (only for fields on screen).
    const moneyFields = ['assetsUnderManagement', 'feeBasedRevenue', 'commissionRevenue'];
    if (showTrailsRevenue) moneyFields.push('trailerRevenue');
    if (showDirectRevenue) moneyFields.push('directRevenue');
    moneyFields.forEach((field) => {
      if (formData[field] > limits[field]) {
        newErrors[field] = intl.formatMessage(
          { id: 'succession.partial_sales.financial_details.error_max_value' },
          { max: formatLimit(limits[field], false) }
        );
      }
    });

    setErrors(newErrors);
    const hasErrors = Object.keys(newErrors).length > 0;

    // Notify parent component about validation state
    if (onValidationChange && lastReportedHasErrors.current !== hasErrors) {
      lastReportedHasErrors.current = hasErrors;
      onValidationChange(hasErrors);
    }

    return !hasErrors;
  };

  // Runs on mount too.
  useEffect(() => {
    validateForm();
  }, [formData]);

  const handleInputChange = (name, value) => {
    if (name === 'numberOfClients') {
      setFormData({
        ...formData,
        [name]: parseInt(value) || 0
      });
    } else {
      setFormData({
        ...formData,
        [name]: parseFloat(value) || 0
      });
    }
  };



  const inputClasses = "w-full px-3 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-succession-primary focus:border-transparent transition-colors bg-succession-bg-primary text-succession-text-primary";
  const labelClasses = "block text-sm font-medium mb-2 text-succession-text-primary";
  const errorClasses = "mt-1 text-sm text-succession-error";

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4 text-succession-text-primary transition-colors duration-300">
          <FormattedMessage id="succession.partial_sales.financial_details.title" />
        </h2>
        <p className="text-lg mb-6 text-succession-text-secondary transition-colors duration-300">
          <FormattedMessage id="succession.partial_sales.financial_details.intro" />
        </p>
      </div>

      {/* Number of Relationships */}
      <div>
        <FinancialInput
          label={intl.formatMessage({ id: 'succession.partial_sales.financial_details.number_of_relationships_label' })}
          name="numberOfClients"
          value={formData.numberOfClients}
          onChange={handleInputChange}
          prefix=""
          isNumber={true}
          min={1}
          max={limits.numberOfClients}
          minLabel={intl.formatMessage({ id: 'succession.partial_sales.financial_details.min_label' })}
          maxLabel={intl.formatMessage({ id: 'succession.partial_sales.financial_details.max_label' })}
        />
        {errors.numberOfClients && (
          <p className={errorClasses}>{errors.numberOfClients}</p>
        )}
      </div>

      {/* Assets Under Management / Total Premium */}
      <div>
        <FinancialInput
          label={partialSalesPropertyCasualty ? intl.formatMessage({ id: 'succession.partial_sales.financial_details.total_premium_label' }) : intl.formatMessage({ id: 'succession.partial_sales.financial_details.assets_under_management_label' })}
          name="assetsUnderManagement"
          value={formData.assetsUnderManagement}
          onChange={handleInputChange}
          prefix="$"
          min={0}
          max={limits.assetsUnderManagement}
          minLabel={intl.formatMessage({ id: 'succession.partial_sales.financial_details.min_label' })}
          maxLabel={intl.formatMessage({ id: 'succession.partial_sales.financial_details.max_label' })}
        />
        {errors.assetsUnderManagement && (
          <p className={errorClasses}>{errors.assetsUnderManagement}</p>
        )}
      </div>

      {/* Revenue Section */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-succession-text-primary transition-colors duration-300">
          <FormattedMessage id="succession.partial_sales.financial_details.revenue_breakdown_title" />
        </h3>
        <p className="text-sm mb-4 text-succession-text-secondary transition-colors duration-300">
          <FormattedMessage id="succession.partial_sales.financial_details.revenue_breakdown_intro" />
        </p>
        
        {errors.revenue && (
          <div className="mb-4 p-3 bg-succession-error-bg border border-succession-error rounded-lg">
            <p className="text-sm text-succession-error">{errors.revenue}</p>
          </div>
        )}

        {/* Fee-Based Revenue / Recurring Commission Revenue */}
        <div>
          <FinancialInput
            label={partialSalesPropertyCasualty ? intl.formatMessage({ id: 'succession.partial_sales.financial_details.recurring_commission_label' }) : intl.formatMessage({ id: 'succession.partial_sales.financial_details.fee_based_revenue_label' })}
            name="feeBasedRevenue"
            value={formData.feeBasedRevenue}
            onChange={handleInputChange}
            prefix="$"
            min={0}
            max={limits.feeBasedRevenue}
            minLabel={intl.formatMessage({ id: 'succession.partial_sales.financial_details.min_label' })}
            maxLabel={intl.formatMessage({ id: 'succession.partial_sales.financial_details.max_label' })}
          />
          {errors.feeBasedRevenue && <p className={errorClasses}>{errors.feeBasedRevenue}</p>}
        </div>

        {/* Trails Revenue - Only show if NOT Property & Casualty */}
        {showTrailsRevenue && (
          <div>
            <FinancialInput
              label={intl.formatMessage({ id: 'succession.partial_sales.financial_details.trails_revenue_label' })}
              name="trailerRevenue"
              value={formData.trailerRevenue}
              onChange={handleInputChange}
              prefix="$"
              min={0}
              max={limits.trailerRevenue}
              minLabel={intl.formatMessage({ id: 'succession.partial_sales.financial_details.min_label' })}
              maxLabel={intl.formatMessage({ id: 'succession.partial_sales.financial_details.max_label' })}
            />
            {errors.trailerRevenue && <p className={errorClasses}>{errors.trailerRevenue}</p>}
          </div>
        )}

        {/* Direct Revenue - Only show if NOT Property & Casualty AND feature flag is not set */}
        {showDirectRevenue && (
          <div>
            <FinancialInput
              label={intl.formatMessage({ id: 'succession.partial_sales.financial_details.direct_revenue_label' })}
              name="directRevenue"
              value={formData.directRevenue}
              onChange={handleInputChange}
              prefix="$"
              min={0}
              max={limits.directRevenue}
              minLabel={intl.formatMessage({ id: 'succession.partial_sales.financial_details.min_label' })}
              maxLabel={intl.formatMessage({ id: 'succession.partial_sales.financial_details.max_label' })}
            />
            {errors.directRevenue && <p className={errorClasses}>{errors.directRevenue}</p>}
          </div>
        )}

        {/* Commission Revenue / New Commission Revenue */}
        <div>
          <FinancialInput
            label={partialSalesPropertyCasualty ? intl.formatMessage({ id: 'succession.partial_sales.financial_details.new_commission_label' }) : intl.formatMessage({ id: 'succession.partial_sales.financial_details.commission_revenue_label' })}
            name="commissionRevenue"
            value={formData.commissionRevenue}
            onChange={handleInputChange}
            prefix="$"
            min={0}
            max={limits.commissionRevenue}
            minLabel={intl.formatMessage({ id: 'succession.partial_sales.financial_details.min_label' })}
            maxLabel={intl.formatMessage({ id: 'succession.partial_sales.financial_details.max_label' })}
          />
          {errors.commissionRevenue && <p className={errorClasses}>{errors.commissionRevenue}</p>}
        </div>
      </div>

      {/* Total Revenue Display */}
      <div className="bg-succession-bg-secondary p-4 rounded-lg border border-succession-border">
        <div className="flex justify-between items-center">
          <span className="text-lg font-semibold text-succession-text-primary transition-colors duration-300">
            <FormattedMessage id="succession.partial_sales.financial_details.total_annual_revenue" />
          </span>
          <span className="text-xl font-bold text-succession-success">
            ${(() => {
              let total = (formData.feeBasedRevenue || 0) + (formData.commissionRevenue || 0);
              
              // Add conditional revenue fields
              if (!partialSalesPropertyCasualty) {
                total += (formData.trailerRevenue || 0);
                if (!company?.feature_types?.includes('hide_direct_revenue')) {
                  total += (formData.directRevenue || 0);
                }
              }
              
              return total.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
            })()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default FinancialDetails;